import { Navigate, useSearchParams } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';

import { Validations } from './index';

/**
 * Route wrapper for the Validations ("Detections") page.
 *
 * The four benchmark views (attack/defense/emb3d/csf) moved to
 * Insights → Benchmarks. Old `/:userId/validations?tab=<framework>` deep links
 * redirect to `/:userId/insights?tab=benchmarks&benchmark=<framework>`; every
 * other `?tab=` value (or none) renders Validations unchanged.
 *
 * Lives outside `Validations()` so the redirect can early-return before that
 * component's hooks run (react-hooks/rules-of-hooks forbids the in-page guard).
 */

// Old Validations benchmark tabs now live under Insights. Fixed whitelist —
// values are constants, never interpolated from user input (SR-11).
const BENCHMARK_REDIRECTS: Record<string, string> = {
  attack: 'attack',
  defense: 'defense',
  emb3d: 'emb3d',
  csf: 'csf',
};

export function ValidationsRoute() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  // Object.hasOwn guard: a plain-object index would admit prototype-chain keys
  // (?tab=constructor, __proto__, toString, …) and fire the redirect with a
  // garbage benchmark value (security review F-1).
  const benchmark =
    tab !== null && Object.hasOwn(BENCHMARK_REDIRECTS, tab)
      ? BENCHMARK_REDIRECTS[tab]
      : undefined;
  if (benchmark) {
    const search = new URLSearchParams(); // SR-11: URLSearchParams, no concat
    search.set('tab', 'benchmarks');
    search.set('benchmark', benchmark); // value from whitelist, not raw param
    return (
      <Navigate
        to={{
          // SR-12: route constant, tenant-relative (resolves to /:userId/insights)
          pathname: `../${AuthenticatedRoute.INSIGHTS}`,
          // SR-13: only tab+benchmark cross the redirect; hostile params dropped
          search: `?${search.toString()}`,
        }}
        replace // SR-14
      />
    );
  }
  return <Validations />;
}
